import Matter from 'matter-js';
import { api } from '../api.js';
import { createSurface, resizeToFullscreen } from '../../render/canvas.js';
import { createPhysicsWorld } from '../../physics/engine.js';

function navigate(path: string): void {
  history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

type ModalIntent = 'browse' | 'create';

// Height each jar is drawn at on the home pile. Width is derived from the
// loaded PNG's natural aspect ratio so jars never stretch.
const JAR_DISPLAY_HEIGHT = 84;

// The homepage pile is the product's visual identity — keep it populated with
// a random 40–50 jars per refresh on desktop, and a lighter 15–25 on mobile so
// the pile doesn't crowd the hero and CTA (and physics stays smooth on
// low-end phones). Real public jars are always shown; if there are fewer than
// the random target, decorative (non-clickable) jars pad it.
// The 720px breakpoint matches the `@media (max-width: 720px)` blocks in
// styles.css so CSS and JS mobile behavior stay aligned.
const IS_NARROW_VIEWPORT = window.innerWidth <= 720;
const PILE_MIN = IS_NARROW_VIEWPORT ? 15 : 40;
const PILE_MAX = IS_NARROW_VIEWPORT ? 25 : 50;

// Per-jar size variation so the pile has natural visual rhythm instead of
// looking like stamped-out duplicates. Sprite height = JAR_DISPLAY_HEIGHT * scale.
const SCALE_MIN = 0.7;
const SCALE_MAX = 1.3;

type HomeJarBody = {
  body: Matter.Body;
  width: number;
  height: number;
  interactive: boolean;
  name?: string;
  label?: string;
};

function openMyJarModal(): void {
  const modal = document.getElementById('myjar-modal') as HTMLDialogElement | null;
  if (!modal) return;
  modal.removeAttribute('hidden');
  if (!modal.hasAttribute('open')) {
    modal.setAttribute('open', 'true');
  }
}

function closeMyJarModal(): void {
  const modal = document.getElementById('myjar-modal') as HTMLDialogElement | null;
  if (!modal) return;
  modal.removeAttribute('open');
  modal.setAttribute('hidden', 'true');
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export async function renderHomeView(root: HTMLElement): Promise<void> {
  const [jars, currentUser] = await Promise.all([api.listPublicJars(), api.me()]);
  root.innerHTML = `
    <section class="home-canvas-shell">
      <button id="myjar-button" class="myjar-corner-link">${currentUser ? 'My Jars' : 'Login'}</button>
      <canvas id="home-canvas" class="home-canvas"></canvas>
      <div class="home-hero" aria-label="TextJar homepage hero">
        <img class="home-logo-jar" src="/logo/textjar-logo.png" alt="" aria-hidden="true" />
        <button id="make-jar-button" class="hero-cta">Create a Jar</button>
      </div>
    </section>

    <dialog id="myjar-modal" class="myjar-modal" hidden>
      <section class="myjar-card" role="document">
        <div id="myjar-content"></div>
      </section>
    </dialog>
  `;

  const canvasEl = root.querySelector<HTMLCanvasElement>('#home-canvas');
  const modalEl = root.querySelector<HTMLDialogElement>('#myjar-modal');
  const myjarCard = root.querySelector<HTMLElement>('#myjar-modal .myjar-card');
  const myjarContent = root.querySelector<HTMLElement>('#myjar-content');
  if (!canvasEl || !modalEl || !myjarCard || !myjarContent) return;
  const myjarCardEl = myjarCard;
  const myjarContentEl = myjarContent;

  function setPanelMode(mode: 'grid' | 'form'): void {
    myjarCardEl.classList.toggle('myjar-card--form', mode === 'form');
  }

  // ----- Physics pile of jars -----
  const surface = createSurface(canvasEl);
  const physics = createPhysicsWorld(window.innerWidth, window.innerHeight);
  resizeToFullscreen(surface, () => physics.resize(surface.width, surface.height));
  // The shared physics world includes a ceiling wall at y ∈ [-300, 0] so that
  // devicemotion shaking on jar pages can't fling letters off the top of the
  // viewport. On the homepage, jars spawn *above* the viewport and fall down —
  // the ceiling would collide with every spawn and push jars sideways, which
  // read as jittery "unnatural" motion. Remove it here.
  const ceiling = physics.walls.find((w) => w.label === 'ceiling');
  if (ceiling) Matter.World.remove(physics.engine.world, ceiling);

  // MouseConstraint so users can grab and fling jars around. Click-vs-drag is
  // disambiguated separately below so a short tap navigates to the jar page.
  const mouse = Matter.Mouse.create(canvasEl);
  mouse.pixelRatio = window.devicePixelRatio || 1;
  const mouseConstraint = Matter.MouseConstraint.create(physics.engine, {
    mouse,
    constraint: { stiffness: 0.18, damping: 0.08, render: { visible: false } },
  });
  Matter.World.add(physics.engine.world, mouseConstraint);

  const jarBodies: HomeJarBody[] = [];
  let stopped = false;

  // Preload the jar image so the first rendered frame has real pixels and we
  // can size the rectangle bodies to match its true aspect ratio.
  const jarImg = new Image();
  jarImg.src = '/logo/jar-logo.png';
  try {
    await jarImg.decode();
  } catch {
    await new Promise<void>((resolve) => {
      if (jarImg.complete) return resolve();
      jarImg.onload = () => resolve();
      jarImg.onerror = () => resolve();
    });
  }
  if (stopped) return;
  const naturalW = jarImg.naturalWidth || 686;
  const naturalH = jarImg.naturalHeight || 817;
  const JAR_H = JAR_DISPLAY_HEIGHT;
  const JAR_W = JAR_H * (naturalW / naturalH);
  // Physics body tighter than the sprite so piles don't show visible gaps —
  // the jar silhouette has a narrow neck and chamfered corners already help.
  const BODY_W = JAR_W * 0.84;
  const BODY_H = JAR_H * 0.92;

  // Build the spawn queue: all real public jars (clickable) + decorative jars
  // padding to hit a random 20–40 pile size. Real jars are never dropped, so
  // if the DB has more than the random target, the pile just grows to match.
  type SpawnItem =
    | { kind: 'real'; name: string; label: string }
    | { kind: 'deco' };
  const queue: SpawnItem[] = jars.map((jar) => ({
    kind: 'real',
    name: jar.name,
    label: jar.title || jar.name,
  }));
  const randomTarget = PILE_MIN + Math.floor(Math.random() * (PILE_MAX - PILE_MIN + 1));
  const totalCount = Math.max(queue.length, randomTarget);
  const decoPad = totalCount - queue.length;
  for (let i = 0; i < decoPad; i++) queue.push({ kind: 'deco' });

  // Stagger the drops so jars cascade in instead of thudding down as a wall.
  // With no ceiling in the world, we can spawn well above the viewport so the
  // fall develops a natural terminal-ish velocity before landing.
  const SPAWN_INTERVAL_MS = 60;
  const spawnOne = (index: number) => {
    if (stopped || index >= queue.length) return;
    const item = queue[index]!;
    const scale = SCALE_MIN + Math.random() * (SCALE_MAX - SCALE_MIN);
    const jarW = JAR_W * scale;
    const jarH = JAR_H * scale;
    const bodyW = BODY_W * scale;
    const bodyH = BODY_H * scale;
    const marginX = jarW * 0.6;
    const x = marginX + Math.random() * Math.max(1, surface.width - marginX * 2);
    // Spawns well above the viewport so each jar has drop distance to look
    // natural rather than teleporting in.
    const y = -jarH * 2 - Math.random() * 400;
    const body = Matter.Bodies.rectangle(x, y, bodyW, bodyH, {
      restitution: 0.12,
      friction: 0.55,
      frictionStatic: 0.6,
      // Density stays constant across sizes so mass scales with area — bigger
      // jars naturally feel heavier and push smaller ones around in the pile.
      density: 0.0016,
      chamfer: { radius: 6 * scale },
    });
    // Small per-body variation so identical rows don't land in a rigid lattice.
    Matter.Body.setAngularVelocity(body, (Math.random() - 0.5) * 0.08);
    Matter.Body.setVelocity(body, { x: (Math.random() - 0.5) * 0.4, y: 0 });
    Matter.World.add(physics.engine.world, body);
    if (item.kind === 'real') {
      jarBodies.push({
        body,
        width: jarW,
        height: jarH,
        interactive: true,
        name: item.name,
        label: item.label,
      });
    } else {
      jarBodies.push({ body, width: jarW, height: jarH, interactive: false });
    }
    window.setTimeout(() => spawnOne(index + 1), SPAWN_INTERVAL_MS);
  };
  spawnOne(0);

  // Click vs drag: navigate only when the press was short and didn't move —
  // otherwise the user is flinging a jar, not picking one.
  let pressStart: { x: number; y: number; t: number } | null = null;
  canvasEl.addEventListener('pointerdown', (e) => {
    pressStart = { x: e.clientX, y: e.clientY, t: performance.now() };
  });
  canvasEl.addEventListener('pointerup', (e) => {
    if (!pressStart) return;
    const dx = e.clientX - pressStart.x;
    const dy = e.clientY - pressStart.y;
    const dt = performance.now() - pressStart.t;
    pressStart = null;
    if (dt > 400 || Math.hypot(dx, dy) > 10) return;
    const interactiveBodies = jarBodies
      .filter((j) => j.interactive)
      .map((j) => j.body);
    const hits = Matter.Query.point(interactiveBodies, { x: e.clientX, y: e.clientY });
    const hit = hits[0];
    if (!hit) return;
    const jar = jarBodies.find((j) => j.body === hit);
    if (jar?.name) navigate(`/${jar.name}`);
  });

  canvasEl.addEventListener('pointermove', (e) => {
    const interactiveBodies = jarBodies
      .filter((j) => j.interactive)
      .map((j) => j.body);
    const hits = Matter.Query.point(interactiveBodies, { x: e.clientX, y: e.clientY });
    canvasEl.style.cursor = hits.length > 0 ? 'pointer' : 'default';
  });

  // Labels start rendering immediately in the Georgia fallback and swap to
  // Henry once the font loads — awaiting `document.fonts.ready` here would
  // freeze the cascade mid-fall on a cold cache.
  let last = performance.now();
  const step = (now: number) => {
    if (stopped) return;
    const dt = Math.min(32, now - last);
    last = now;
    Matter.Engine.update(physics.engine, dt);

    surface.clear('transparent');
    const ctx = surface.ctx;
    for (const { body, width, height } of jarBodies) {
      ctx.save();
      ctx.translate(body.position.x, body.position.y);
      ctx.rotate(body.angle);
      ctx.drawImage(jarImg, -width / 2, -height / 2, width, height);
      ctx.restore();
    }
    // Labels drawn unrotated below each jar so they stay upright and readable
    // regardless of how the jar settled in the pile. Decorative jars have no
    // label — they're purely visual.
    ctx.save();
    ctx.font = '14px "Henry", Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = 'rgba(25, 25, 25, 0.82)';
    for (const { body, height, label } of jarBodies) {
      if (!label) continue;
      ctx.fillText(label, body.position.x, body.position.y + height / 2 + 4);
    }
    ctx.restore();

    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);

  const onPop = () => {
    stopped = true;
    window.removeEventListener('popstate', onPop);
  };
  window.addEventListener('popstate', onPop);

  const logoEl = root.querySelector<HTMLImageElement>('.home-logo-jar');
  if (logoEl) {
    let currentLogoState: 'left' | 'right' | 'rock' | 'rest' = 'rest';
    const setLogoState = (state: 'left' | 'right' | 'rock' | 'rest') => {
      if (state === currentLogoState) return;
      currentLogoState = state;
      logoEl.classList.toggle('tilt-left', state === 'left');
      logoEl.classList.toggle('tilt-right', state === 'right');
      logoEl.classList.toggle('rocking', state === 'rock');
    };
    logoEl.addEventListener('pointermove', (event) => {
      const rect = logoEl.getBoundingClientRect();
      const ratio = (event.clientX - rect.left) / rect.width;
      if (ratio < 0.33) setLogoState('right');
      else if (ratio > 0.67) setLogoState('left');
      else setLogoState('rock');
    });
    logoEl.addEventListener('pointerleave', () => setLogoState('rest'));
  }

  function closeButtonMarkup(): string {
    return `<button class="myjar-close" data-close aria-label="Close">&times;</button>`;
  }

  async function renderBrowsePanel(): Promise<void> {
    const myJars = await api.listMyJars();
    setPanelMode(myJars.length ? 'grid' : 'form');
    myjarContentEl.innerHTML = `
      <header class="myjar-header">
        <h2>My Jars</h2>
        ${closeButtonMarkup()}
      </header>
      ${
        myJars.length
          ? `<div class="myjar-grid">
              ${myJars
                .map((jar) => {
                  const label = escapeHtml(jar.title || jar.name);
                  return `
                    <div class="myjar-tile" data-jar-id="${escapeHtml(jar.id)}">
                      <button type="button" class="myjar-tile-open" data-open>
                        <img class="myjar-tile-icon" src="/logo/jar-logo.png" alt="" aria-hidden="true" />
                        <span class="myjar-tile-name">${label}</span>
                      </button>
                      <button type="button" class="myjar-tile-delete" data-delete aria-label="Delete ${label}">&times;</button>
                      <div class="myjar-tile-confirm" aria-hidden="true">
                        <span class="myjar-tile-confirm-label">Delete?</span>
                        <div class="myjar-tile-confirm-row">
                          <button type="button" class="secondary myjar-tile-confirm-btn" data-cancel>Cancel</button>
                          <button type="button" class="danger myjar-tile-confirm-btn" data-confirm>Delete</button>
                        </div>
                      </div>
                    </div>
                  `;
                })
                .join('')}
            </div>`
          : `<p class="myjar-empty">No jars yet — create your first one.</p>`
      }
      <footer class="myjar-footer">
        <button type="button" id="open-create" class="myjar-create-btn">Create a Jar</button>
      </footer>
    `;

    myjarContentEl.querySelectorAll<HTMLElement>('.myjar-tile').forEach((tile) => {
      const id = tile.dataset.jarId;
      if (!id) return;
      const jar = myJars.find((j) => j.id === id);
      if (!jar) return;

      tile.querySelector<HTMLButtonElement>('[data-open]')?.addEventListener('click', () => {
        closeMyJarModal();
        navigate(`/${jar.name}`);
      });

      tile.querySelector<HTMLButtonElement>('[data-delete]')?.addEventListener('click', () => {
        tile.classList.add('is-confirming');
      });

      tile.querySelector<HTMLButtonElement>('[data-cancel]')?.addEventListener('click', () => {
        tile.classList.remove('is-confirming');
      });

      tile.querySelector<HTMLButtonElement>('[data-confirm]')?.addEventListener('click', async () => {
        try {
          await api.deleteJar(jar.id);
          await renderBrowsePanel();
        } catch (err) {
          alert(err instanceof Error ? err.message : 'Unable to delete jar');
          tile.classList.remove('is-confirming');
        }
      });
    });

    myjarContentEl
      .querySelector<HTMLButtonElement>('#open-create')
      ?.addEventListener('click', () => renderCreatePanel());

    wireCloseButtons();
  }

  function renderCreatePanel(): void {
    setPanelMode('form');
    myjarContentEl.innerHTML = `
      <header class="myjar-header">
        <h2>Create a Jar</h2>
        ${closeButtonMarkup()}
      </header>
      <form id="create-jar-form" class="myjar-form">
        <label>Name
          <input required name="title" placeholder="e.g. Friend Notes" minlength="2" maxlength="80" autocomplete="off" />
        </label>
        <label>Description
          <input name="description" placeholder="A short note shown on the jar page (optional)" maxlength="200" autocomplete="off" />
        </label>
        <label>URL slug
          <input required name="name" placeholder="friend-notes" pattern="[a-z0-9][a-z0-9-]{1,28}[a-z0-9]" autocomplete="off" />
        </label>
        <label class="row"><input type="checkbox" name="isPublic" checked /> Public jar</label>
        <p id="create-jar-error" class="error"></p>
        <div class="myjar-form-actions">
          <button type="button" class="secondary" data-back>Back</button>
          <button type="submit">Create</button>
        </div>
      </form>
    `;

    const form = myjarContentEl.querySelector<HTMLFormElement>('#create-jar-form');
    const errorEl = myjarContentEl.querySelector<HTMLElement>('#create-jar-error');
    form?.querySelector<HTMLInputElement>('input[name="title"]')?.focus();

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!errorEl) return;
      errorEl.textContent = '';
      const formData = new FormData(form);
      try {
        const jar = await api.createJar({
          name: String(formData.get('name') ?? ''),
          title: String(formData.get('title') ?? ''),
          description: String(formData.get('description') ?? ''),
          isPublic: Boolean(formData.get('isPublic')),
        });
        closeMyJarModal();
        navigate(`/${jar.name}`);
      } catch (err) {
        errorEl.textContent = err instanceof Error ? err.message : 'Unable to create jar';
      }
    });

    myjarContentEl
      .querySelector<HTMLButtonElement>('[data-back]')
      ?.addEventListener('click', () => {
        void renderBrowsePanel();
      });

    wireCloseButtons();
  }

  function renderLoggedOutPanel(): void {
    setPanelMode('form');
    myjarContentEl.innerHTML = `
      <header class="myjar-header">
        <h2>Log in</h2>
        ${closeButtonMarkup()}
      </header>
      <form id="email-auth-form" class="myjar-form">
        <p class="muted">Sign in to create jars and collect messages.</p>
        <label>Email <input required type="email" name="email" autocomplete="email" /></label>
        <label>Password <input required type="password" minlength="8" name="password" autocomplete="current-password" /></label>
        <p id="auth-error" class="error"></p>
        <div class="myjar-form-actions">
          <button type="button" id="google-login-button" class="secondary">Continue with Google</button>
          <button type="submit" data-mode="signup" class="secondary">Register</button>
          <button type="submit" data-mode="login">Log in</button>
        </div>
      </form>
    `;
    const form = myjarContentEl.querySelector<HTMLFormElement>('#email-auth-form');
    const authError = myjarContentEl.querySelector<HTMLElement>('#auth-error');
    const googleButton = myjarContentEl.querySelector<HTMLButtonElement>('#google-login-button');
    if (!form || !authError || !googleButton) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      authError.textContent = '';
      const submitter = (event as SubmitEvent).submitter as HTMLButtonElement | null;
      const mode = submitter?.dataset.mode ?? 'login';
      const formData = new FormData(form);
      const email = String(formData.get('email') ?? '');
      const password = String(formData.get('password') ?? '');
      try {
        if (mode === 'signup') {
          const { needsConfirmation } = await api.signUpWithEmail(email, password);
          if (needsConfirmation) {
            // Supabase's "Confirm email" setting is on. Don't attempt
            // signInWithPassword — it will fail with "Invalid login credentials"
            // until the user clicks the confirmation link in their inbox.
            authError.classList.remove('error');
            authError.classList.add('success');
            authError.textContent = 'Check your email to confirm your account, then come back and log in.';
            return;
          }
          await api.loginWithEmail(email, password);
        } else {
          await api.loginWithEmail(email, password);
        }
        const cornerButton = root.querySelector<HTMLButtonElement>('#myjar-button');
        if (cornerButton) cornerButton.textContent = 'My Jars';
        await renderBrowsePanel();
      } catch (err) {
        authError.classList.remove('success');
        authError.classList.add('error');
        authError.textContent = err instanceof Error ? err.message : 'Authentication failed';
      }
    });

    googleButton.addEventListener('click', async () => {
      await api.loginWithGoogle();
    });

    wireCloseButtons();
  }

  function wireCloseButtons(): void {
    myjarContentEl.querySelectorAll<HTMLButtonElement>('[data-close]').forEach((btn) => {
      btn.addEventListener('click', closeMyJarModal);
    });
  }

  async function openModalWithIntent(intent: ModalIntent): Promise<void> {
    const me = await api.me();
    if (!me) {
      renderLoggedOutPanel();
    } else if (intent === 'create') {
      renderCreatePanel();
    } else {
      await renderBrowsePanel();
    }
    openMyJarModal();
  }

  modalEl.addEventListener('click', (event) => {
    if (event.target === modalEl) closeMyJarModal();
  });

  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modalEl.hasAttribute('open')) closeMyJarModal();
  });

  root.querySelector<HTMLButtonElement>('#myjar-button')?.addEventListener('click', () => {
    void openModalWithIntent('browse');
  });

  root.querySelector<HTMLButtonElement>('#make-jar-button')?.addEventListener('click', () => {
    void openModalWithIntent('create');
  });
}
